// components/features/tournaments/components/PlayerStatsComparisonGrid.jsx
import React, { useState, useMemo } from 'react';
import { 
  Users, 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Eye,
  EyeOff,
  Target,
  Award
} from '../../../common/Icons';
import UserAvatar from '../../../common/UserAvatar';
import { FIELD_COMPARISON } from '../../../../utils/comparisonEngine';

const PlayerStatsComparisonGrid = ({ playerStatsComparison, team1Name, team2Name }) => {
  const [showOnlyConflicts, setShowOnlyConflicts] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());

  const togglePlayerExpansion = (playerId) => {
    const newExpanded = new Set(expandedPlayers);
    if (newExpanded.has(playerId)) {
      newExpanded.delete(playerId);
    } else {
      newExpanded.add(playerId);
    }
    setExpandedPlayers(newExpanded);
  };

  const filteredPlayers = useMemo(() => {
    if (!playerStatsComparison?.playerComparisons) return [];
    
    return playerStatsComparison.playerComparisons.filter(player => {
      if (showOnlyConflicts) {
        return player.status === FIELD_COMPARISON.DIFFER || player.status === FIELD_COMPARISON.MISSING;
      }
      return true;
    });
  }, [playerStatsComparison, showOnlyConflicts]);

  const getStatusIcon = (status) => {
    switch (status) {
      case FIELD_COMPARISON.MATCH:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case FIELD_COMPARISON.DIFFER:
        return <XCircle className="w-4 h-4 text-red-500" />;
      case FIELD_COMPARISON.MISSING:
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getDifferenceIndicator = (value, otherValue) => {
    if (value === otherValue) {
      return <Minus className="w-3 h-3 text-gray-400" />;
    }
    
    if (value > otherValue) {
      return <TrendingUp className="w-3 h-3 text-green-500" />;
    }
    
    return <TrendingDown className="w-3 h-3 text-red-500" />;
  };

  const formatPlayerName = (playerStats) => {
    // Try to get player name from various possible fields
    return playerStats?.player_name || 
           playerStats?.username || 
           playerStats?.display_name || 
           `Player ${playerStats?.player_id?.slice(-4) || '???'}`;
  };

  const StatField = ({ label, team1Value = 0, team2Value = 0, fieldComparison, icon: Icon }) => {
    const isMatch = fieldComparison?.status === FIELD_COMPARISON.MATCH;
    const difference = team2Value - team1Value;
    
    return (
      <div className={`p-3 rounded-lg border ${
        isMatch
          ? 'border-green-500/40 bg-green-900/20'
          : 'border-red-500/40 bg-red-900/20'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {Icon && <Icon className="w-4 h-4 text-gray-400" />}
            <span className="text-xs font-medium text-gray-300">
              {label}
            </span>
          </div>
          {getStatusIcon(fieldComparison?.status)}
        </div>

        <div className="grid grid-cols-3 gap-2 items-center">
          {/* Team 1 Value */}
          <div className="text-center">
            <div className="text-base font-bold text-white">
              {team1Value}
            </div>
            <div className="text-xs text-gray-400">
              {team1Name}
            </div>
          </div>

          {/* Difference Indicator */}
          <div className="text-center">
            {difference !== 0 && (
              <div className="flex flex-col items-center">
                {getDifferenceIndicator(team1Value, team2Value)}
                <span className={`text-xs font-medium ${
                  difference > 0 ? 'text-red-400' :
                  difference < 0 ? 'text-green-400' :
                  'text-gray-400'
                }`}>
                  {difference > 0 ? '+' : ''}{difference}
                </span>
              </div>
            )}
          </div>

          {/* Team 2 Value */}
          <div className="text-center">
            <div className="text-base font-bold text-white">
              {team2Value}
            </div>
            <div className="text-xs text-gray-400">
              {team2Name}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!playerStatsComparison || !playerStatsComparison.playerComparisons) {
    return (
      <div className="p-4 text-center text-gray-400">
        <Users className="w-6 h-6 mx-auto mb-2" />
        <p className="text-sm">No player statistics to compare</p>
        <p className="text-xs mt-2">
          This may be a partial submission where only one team has submitted results.
        </p>
      </div>
    );
  }

  const { summary } = playerStatsComparison;

  const isPartialSubmission = playerStatsComparison?.isPartialSubmission;
  const submittingTeam = playerStatsComparison?.submittingTeam;

  return (
    <div className="space-y-3">
      {/* Header with Summary and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-base font-bold text-white flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>
              {isPartialSubmission ? 'Player Statistics Review' : 'Player Statistics Comparison'}
            </span>
          </h3>

          {/* Summary Stats */}
          <div className="flex items-center space-x-4 text-sm">
            {isPartialSubmission ? (
              <div className="flex items-center space-x-1">
                <CheckCircle className="w-4 h-4 text-[#23F8B5]" />
                <span className="text-gray-400">
                  {summary?.totalPlayers || 0} players submitted by {submittingTeam === 'team1' ? team1Name : team2Name}
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-gray-400">
                    {summary?.matchingPlayers || 0} match
                  </span>
                </div>

                {summary?.conflictedPlayers > 0 && (
                  <div className="flex items-center space-x-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-gray-400">
                      {summary.conflictedPlayers} conflicts
                    </span>
                  </div>
                )}

                {summary?.missingPlayers > 0 && (
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    <span className="text-gray-400">
                      {summary.missingPlayers} missing
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowOnlyConflicts(!showOnlyConflicts)}
          className={`flex items-center space-x-2 px-3 py-1 rounded-lg border transition-colors ${
            showOnlyConflicts
              ? 'bg-red-900/20 border-red-500/40 text-red-300'
              : 'bg-[#22262f] border-white/10 text-gray-300'
          }`}
        >
          {showOnlyConflicts ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          <span className="text-sm">
            {showOnlyConflicts ? 'Show All' : 'Conflicts Only'}
          </span>
        </button>
      </div>

      {/* Players List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredPlayers.map((playerComparison) => {
          const isExpanded = expandedPlayers.has(playerComparison.playerId);
          const isMissing = playerComparison.status === FIELD_COMPARISON.MISSING;
          
          return (
            <div
              key={playerComparison.playerId}
              className={`border rounded-xl overflow-hidden transition-all ${
                playerComparison.status === FIELD_COMPARISON.MATCH
                  ? 'border-green-500/40'
                  : playerComparison.status === FIELD_COMPARISON.DIFFER
                    ? 'border-red-500/40'
                    : 'border-yellow-500/40'
              }`}
            >
              {/* Player Header */}
              <div
                className={`p-3 cursor-pointer transition-colors ${
                  playerComparison.status === FIELD_COMPARISON.MATCH
                    ? 'bg-green-900/20 hover:bg-green-900/30'
                    : playerComparison.status === FIELD_COMPARISON.DIFFER
                      ? 'bg-red-900/20 hover:bg-red-900/30'
                      : 'bg-yellow-900/20 hover:bg-yellow-900/30'
                }`}
                onClick={() => togglePlayerExpansion(playerComparison.playerId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(playerComparison.status)}
                    
                    {/* Player Avatar and Name */}
                    <div className="flex items-center space-x-2">
                      <UserAvatar
                        user={{
                          id: playerComparison.playerId,
                          avatar_url: playerComparison.team1Stats?.player_avatar || playerComparison.team2Stats?.player_avatar,
                          username: formatPlayerName(playerComparison.team1Stats || playerComparison.team2Stats)
                        }}
                        size="w-8 h-8"
                        textSize="text-sm"
                        clickable={false}
                      />
                      <div>
                        <p className="font-medium text-white">
                          {formatPlayerName(playerComparison.team1Stats || playerComparison.team2Stats)}
                        </p>

                        {/* Show team name for the player */}
                        <p className="text-xs text-gray-400">
                          {(playerComparison.team1Stats || playerComparison.team2Stats)?.team_name ||
                           (playerComparison.availableStats?.team_name)}
                        </p>

                        {isMissing && (
                          <p className="text-xs text-yellow-400">
                            Missing from {playerComparison.missingFrom === 'team1' ? team1Name : team2Name} submission
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats Preview */}
                  {!isMissing && (
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="text-center">
                        <div className="font-bold text-white">
                          {(playerComparison.team1Stats?.goals || playerComparison.team2Stats?.goals) || 0}
                        </div>
                        <div className="text-xs text-gray-400">Goals</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="font-bold text-white">
                          {(playerComparison.team1Stats?.assists || playerComparison.team2Stats?.assists) || 0}
                        </div>
                        <div className="text-xs text-gray-400">Assists</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="font-bold text-white">
                          {(playerComparison.team1Stats?.saves || playerComparison.team2Stats?.saves) || 0}
                        </div>
                        <div className="text-xs text-gray-400">Saves</div>
                      </div>
                    </div>
                  )}
                  
                  {/* For partial submissions, show available stats */}
                  {isMissing && playerComparison.availableStats && (
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="text-center">
                        <div className="font-bold text-white">
                          {playerComparison.availableStats.goals || 0}
                        </div>
                        <div className="text-xs text-gray-400">Goals</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="font-bold text-white">
                          {playerComparison.availableStats.assists || 0}
                        </div>
                        <div className="text-xs text-gray-400">Assists</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="font-bold text-white">
                          {playerComparison.availableStats.saves || 0}
                        </div>
                        <div className="text-xs text-gray-400">Saves</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && !isMissing && (
                <div className="p-3 border-t border-white/10 bg-[#1e2129]">
                  {/* Player Team Header */}
                  <div className="mb-2 p-2 bg-[#22262f] rounded">
                    <p className="text-xs font-medium text-gray-300">
                      Team: {(playerComparison.team1Stats || playerComparison.team2Stats)?.team_name ||
                             (playerComparison.availableStats?.team_name) || 'Unknown Team'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <StatField
                      label="Goals"
                      team1Value={playerComparison.team1Stats?.goals || 0}
                      team2Value={playerComparison.team2Stats?.goals || 0}
                      fieldComparison={playerComparison.fieldComparisons?.goals}
                      icon={Target}
                    />
                    
                    <StatField
                      label="Assists"
                      team1Value={playerComparison.team1Stats?.assists || 0}
                      team2Value={playerComparison.team2Stats?.assists || 0}
                      fieldComparison={playerComparison.fieldComparisons?.assists}
                      icon={Award}
                    />
                    
                    <StatField
                      label="Saves"
                      team1Value={playerComparison.team1Stats?.saves || 0}
                      team2Value={playerComparison.team2Stats?.saves || 0}
                      fieldComparison={playerComparison.fieldComparisons?.saves}
                    />
                    
                    <StatField
                      label="Passes"
                      team1Value={playerComparison.team1Stats?.passes || 0}
                      team2Value={playerComparison.team2Stats?.passes || 0}
                      fieldComparison={playerComparison.fieldComparisons?.passes}
                    />
                    
                    <StatField
                      label="Interceptions"
                      team1Value={playerComparison.team1Stats?.interceptions || 0}
                      team2Value={playerComparison.team2Stats?.interceptions || 0}
                      fieldComparison={playerComparison.fieldComparisons?.interceptions}
                    />
                  </div>
                </div>
              )}

              {/* Missing Player Details */}
              {isExpanded && isMissing && (
                <div className="p-3 border-t border-white/10 bg-[#1e2129]">
                  <div className="text-center text-gray-400">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-sm">
                      This player appears in {playerComparison.missingFrom === 'team1' ? team2Name : team1Name}'s
                      submission but not in {playerComparison.missingFrom === 'team1' ? team1Name : team2Name}'s submission.
                    </p>

                    {/* Show available stats */}
                    {playerComparison.availableStats && (
                      <div className="mt-2 p-3 bg-[#22262f] rounded-lg">
                        <p className="text-xs font-medium mb-2">Available Statistics:</p>
                        <div className="grid grid-cols-5 gap-2 text-xs">
                          <div>
                            <div className="font-bold">{playerComparison.availableStats.goals || 0}</div>
                            <div>Goals</div>
                          </div>
                          <div>
                            <div className="font-bold">{playerComparison.availableStats.assists || 0}</div>
                            <div>Assists</div>
                          </div>
                          <div>
                            <div className="font-bold">{playerComparison.availableStats.saves || 0}</div>
                            <div>Saves</div>
                          </div>
                          <div>
                            <div className="font-bold">{playerComparison.availableStats.passes || 0}</div>
                            <div>Passes</div>
                          </div>
                          <div>
                            <div className="font-bold">{playerComparison.availableStats.interceptions || 0}</div>
                            <div>Interceptions</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredPlayers.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <Users className="w-6 h-6 mx-auto mb-2" />
            <p>
              {showOnlyConflicts 
                ? 'No conflicts found in player statistics' 
                : 'No player statistics available'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerStatsComparisonGrid;